import { useState } from 'react'
import { ReservationTimeNewFormWrapper } from './form/reservation-time-new.form-wrapper'
import { useResourceTypesQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { Button, CircularProgress, Stack, Grid } from '@vapor/material'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutProps } from 'src/app/layout/form-layout/form-layout.types'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { getDatePartOnly, getTimePartOnly } from 'src/utils/date'

type ReservationTimeNewDrawerProps = {
  propertyId: string
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  onSubmitAndOpenReservation?: (reservationId: string) => void
  reservationId?: string
  planningInfo?: {
    resourceTypeId: string
    resourceId?: string
  }
  filter?: {
    start: Date
    end: Date
  }
}

export default function ReservationTimeNewDrawer(props: ReservationTimeNewDrawerProps) {
  const { id: tenantId } = useTenant()
  const localizeString = useLocalizeString()
  const { t } = useTranslation()

  const formLayoutProps: Omit<FormLayoutProps, 'title' | 'toolbarContents' | 'toolbar'> = {
    appearance: {
      type: 'DRAWER',
      open: props.open,
      onClose: () => {
        props.onCancel()
      },
      tight: true,
      titleOnLeft: true,
    },
  }

  const onSubmitComplete = () => {
    props.onConfirm()
  }

  const onCancelHandler = () => {
    props.onCancel()
  }

  const [resourceTypeId, setResourceTypeId] = useState<string | undefined>(props.planningInfo?.resourceTypeId)

  const { data: resourceTypeTime, loading } = useResourceTypesQuery({ variables: { filter: { tenantId: { eq: tenantId }, propertyId: { eq: props.propertyId }, timeResources: true } } })

  return (
    <>
      {loading && <CircularProgress />}
      {!resourceTypeId ? (
        <>
          <FormLayout
            title={t('pms.booking.reservation.reservation_time_creation.title', { timeResourceType: '...' })}
            {...formLayoutProps}
            appearance={{ ...formLayoutProps.appearance }}
            toolbar={
              <Stack direction="row-reverse">
                <Button variant={'outlined'} onClick={() => props.onCancel()}>
                  {t('commons.close')}
                </Button>
              </Stack>
            }
          >
            <Grid container spacing={4}>
              {resourceTypeTime?.resourceTypes?.map((resourceType) => (
                <Grid item xs={3} key={resourceType.id}>
                  <Button
                    variant="outlined"
                    sx={{ '&.MuiButton-sizeMedium': { flexDirection: 'column', height: 'auto !important' } }}
                    fullWidth
                    key={resourceType.id}
                    onClick={() => setResourceTypeId(resourceType.id)}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                  >
                    {localizeString(resourceType.customName)}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </FormLayout>
        </>
      ) : (
        <ReservationTimeNewFormWrapper
          reservationId={props.reservationId}
          formLayoutProps={formLayoutProps}
          propertyId={props.propertyId}
          onSubmitComplete={onSubmitComplete}
          onCancel={onCancelHandler}
          onSubmitAndOpenReservation={props.onSubmitAndOpenReservation}
          resourceTypeId={resourceTypeId}
          resourceId={props.planningInfo?.resourceId}
          start={props.filter?.start ? getDatePartOnly(props.filter.start) : undefined}
          end={props.filter?.end ? getDatePartOnly(props.filter.end) : undefined}
          from={props.filter?.start ? getTimePartOnly(props.filter.start) : undefined}
          to={props.filter?.end ? getTimePartOnly(props.filter.end) : undefined}
        />
      )}
    </>
  )
}
